<template>
    <div>
        <ul class="item-form">
            <li class="form-label">
                {{ $t('Prompt Name') }}
            </li>
            <li>
                <CicTextbox id="media_name" :value="name" :maxlength="256" @input="(val) => (name = val)" />
            </li>
            <li class="form-label">
                {{ $t('Prompt Text') }}
            </li>
            <li>
                <CicTextarea id="media_text" :value="text" :maxlength="4000" @input="(val) => (text = val)" />
            </li>
            <li class="form-label">
                {{ $t('Voice Model Source') }}
            </li>
            <li>
                <CicRadioGroup
                    id="media_source"
                    :value="source"
                    :options="[
                        { value: 'azure', label: 'Azure' },
                        { value: 'google', label: 'Google' },
                    ]"
                    @input="(val) => (source = val)"
                />
            </li>
            <li class="form-label">
                {{ $t('Voice Model Language') }}
            </li>
            <li>
                <CicSelect
                    id="media_language"
                    :value="language"
                    :options="voiceLanguages"
                    @input="(val) => (language = val)"
                />
            </li>
            <li class="form-label">
                {{ $t('Voice Model') }}
            </li>
            <li>
                <CicSelect
                    id="media_model"
                    :value="voiceModel"
                    :options="filteredModels"
                    @input="(val) => (voiceModel = val)"
                />
            </li>
            <li class="form-label">
                {{ $t('Rate of speech (percentage)') }}
            </li>
            <li>
                <ElSlider
                    id="media_rate"
                    :model-value="$store.mediaRate"
                    :min="source === 'azure' ? 50 : 25"
                    :max="source === 'azure' ? 200 : 400"
                    :step="25"
                    :show-input="true"
                    :show-input-controls="true"
                    :show-tooltip="false"
                    @input="setMediaRate"
                />
            </li>
            <li class="form-label">
                {{ $t('Pitch of speech') }}
            </li>
            <li>
                <ElSlider
                    id="media_pitch"
                    :model-value="$store.mediaPitch"
                    :min="-20"
                    :max="20"
                    :show-input="true"
                    :show-input-controls="true"
                    :show-tooltip="false"
                    @input="setMediaPitch"
                />
            </li>
        </ul>
        <div class="cic-footer">
            <CicButton
                id="createPrompt"
                :title="$t('This will create a Wav file using the text entered')"
                @click="handleCreate"
            >
                {{ $t('Create Prompt') }}
            </CicButton>
            <template v-if="$store.hasRight('bUploadMedia') && prompts.length > 0">
                <CicButton
                    id="btTTSDownload"
                    :title="$t('This will download the Wav file')"
                    @click="
                        $store.exportFile({
                            link: 'medialist/export',
                            params: {
                                name,
                                content: text,
                                source,
                                voiceModel,
                                rateOfSpeech: $store.mediaRate,
                                pitchOfSpeech: $store.mediaPitch,
                            },
                        })
                    "
                >
                    {{ $t('Download') }}
                </CicButton>
                <CicButton
                    id="btTTSAddToMediaList"
                    :title="$t('This will add the prompt to the selected media list')"
                    @click="addToMediaList"
                >
                    {{ $t('Add To Media List') }}
                </CicButton>
            </template>
        </div>
    </div>
</template>

<script>
    import { ElSlider } from 'element-plus';
    import CicRadioGroup from '@/components/inputs/CicRadioGroup';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextarea from '@/components/inputs/CicTextarea';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import CicButton from '@/components/CicButton';
    import loadMedia from './MediaUtils';

    export default {
        name: 'CreateMedia',
        components: {
            CicButton,
            CicRadioGroup,
            CicSelect,
            CicTextarea,
            CicTextbox,
            ElSlider,
        },
        props: {
            voiceModels: {
                type: Array,
                required: true,
            },
        },
        emits: ['change'],
        data() {
            return {
                name: '',
                text: '',
                language: null,
                prompts: [],
                source: 'google',
                voiceModel: '',
            };
        },
        computed: {
            filteredModels() {
                return [{ value: '', label: this.$t('Select Voice Model') }].concat(
                    this.voiceModels
                        .filter(
                            (x, index) =>
                                x.languageCode === this.language &&
                                x.source === this.source &&
                                index === this.voiceModels.findIndex((y) => y.name === x.name),
                        )
                        .map((x) => {
                            let label = x.variant;
                            if (x.source === 'google') {
                                let gender = this.$t('Neutral');
                                switch (x.gender) {
                                    case 0:
                                        gender = this.$t('Unspecified');
                                        break;
                                    case 1:
                                        gender = this.$t('Male');
                                        break;
                                    case 2:
                                        gender = this.$t('Female');
                                        break;
                                }
                                label =
                                    gender +
                                    ' ' +
                                    (['Standard', 'Wavenet', 'Neural2'].includes(x.type) ? this.$t(x.type) : x.type) +
                                    ' ' +
                                    x.variant;
                            }
                            return {
                                value: x.name,
                                label: label,
                            };
                        }),
                );
            },
            voiceLanguages() {
                return this.voiceModels
                    .filter((x) => x.source === this.source)
                    .map((x) => x.languageCode)
                    .filter((val, index, self) => self.indexOf(val) === index)
                    .map((x) => ({ value: x, label: this.$t(x) }));
            },
        },
        created() {
            this.language = 'en-GB';
            const locale = this.$i18n.locale;
            if (this.voiceLanguages.some((x) => x.value === locale)) {
                this.language = locale;
            } else if (
                this.voiceLanguages.filter((x) => x.value.substring(0, 3) === locale.substring(0, 3)).length === 1
            ) {
                this.language = this.voiceLanguages.find(
                    (x) => x.value.substring(0, 3) === locale.substring(0, 3),
                ).value;
            }
        },
        methods: {
            addToMediaList() {
                this.$emit('change', this.prompts);
                this.prompts = [];
            },
            handleCreate() {
                this.prompts = [];
                loadMedia(
                    'medialibrary/createprompt',
                    {
                        name: this.name,
                        content: this.text,
                        source: this.source,
                        voiceModel: this.voiceModel,
                        rateOfSpeech: this.$store.mediaRate,
                        pitchOfSpeech: this.$store.mediaPitch,
                    },
                    'all',
                    (response) => {
                        this.prompts = response.prompts;
                    },
                );
            },
            setMediaRate(val) {
                this.$store.setMediaRate(val);
            },
            setMediaPitch(val) {
                this.$store.setMediaPitch(val);
            },
        },
    };
</script>
